import { Fragment, type ReactNode } from "react";

export interface MarkdownLiteProps {
  children: string;
  className?: string;
}

/**
 * Renders the deliberately small Rounds formatting surface as React nodes.
 * React escapes every text node; raw forum input is never interpreted as HTML.
 */
export function MarkdownLite({ children, className }: MarkdownLiteProps) {
  const blocks = parseBlocks(children);
  return (
    <div className={className ?? "pb-rounds-markdown"}>
      {blocks.map((block, index) =>
        block.kind === "list" ? (
          <ul key={`list-${index}`}>
            {block.items.map((item, itemIndex) => (
              <li key={`item-${itemIndex}`}>{renderInline(item)}</li>
            ))}
          </ul>
        ) : (
          <p key={`paragraph-${index}`}>{renderInline(block.text)}</p>
        ),
      )}
    </div>
  );
}

type MarkdownBlock =
  | { kind: "paragraph"; text: string }
  | { kind: "list"; items: string[] };

function parseBlocks(value: string): MarkdownBlock[] {
  const lines = value.replace(/\r\n?/g, "\n").split("\n");
  const blocks: MarkdownBlock[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];

  const flushParagraph = () => {
    if (!paragraph.length) return;
    blocks.push({ kind: "paragraph", text: paragraph.join("\n") });
    paragraph = [];
  };
  const flushList = () => {
    if (!list.length) return;
    blocks.push({ kind: "list", items: list });
    list = [];
  };

  for (const line of lines) {
    if (/^-\s+/.test(line)) {
      flushParagraph();
      list.push(line.replace(/^-\s+/, ""));
      continue;
    }
    if (!line.trim()) {
      flushParagraph();
      flushList();
      continue;
    }
    flushList();
    paragraph.push(line);
  }
  flushParagraph();
  flushList();
  return blocks;
}

function renderInline(value: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const token = /(\*\*[^*\n]+\*\*|\[[^\]\n]+\]\([^\s)]+\))/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = token.exec(value)) !== null) {
    if (match.index > cursor) nodes.push(value.slice(cursor, match.index));
    const raw = match[0];
    if (raw.startsWith("**")) {
      nodes.push(<strong key={`strong-${match.index}`}>{raw.slice(2, -2)}</strong>);
    } else {
      const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(raw);
      const href = link?.[2];
      if (link && href && isSafeHttpsUrl(href)) {
        nodes.push(
          <a
            key={`link-${match.index}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {link[1]}
          </a>,
        );
      } else {
        nodes.push(<Fragment key={`text-${match.index}`}>{raw}</Fragment>);
      }
    }
    cursor = match.index + raw.length;
  }

  if (cursor < value.length) nodes.push(value.slice(cursor));
  return nodes;
}

function isSafeHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}
